import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Gift, History, Trophy, Award, Medal, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { useQuery } from '@tanstack/react-query';

interface Reward {
  id: string;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  points_required: number;
  category: string;
  image_url?: string;
  stock_quantity?: number;
  is_active: boolean;
}

interface UserLevel {
  name: string;
  color: string;
  min_points: number;
  max_points: number;
  benefits: string[];
}

interface RedemptionRecord {
  id: string;
  reward_id: string;
  points_spent: number;
  status: string;
  created_at: string;
  notes?: string;
  rewards: {
    name: string;
    name_en?: string;
    description?: string;
    description_en?: string;
  };
}

interface PointsRule {
  id: string;
  action_type: string;
  action_name: string;
  action_name_en?: string;
  points: number;
  description: string;
  description_en?: string;
  is_active: boolean;
  is_repeatable: boolean;
  created_at: string;
}

const Points = () => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [redemptionHistory, setRedemptionHistory] = useState<RedemptionRecord[]>([]);
  const [pointsRules, setPointsRules] = useState<PointsRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, userProfile, isLoggedIn } = useAuth();
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Fetch user level based on points
  const {
    data: userLevel,
    isLoading: levelLoading
  } = useQuery({
    queryKey: ['user-level', userProfile?.points],
    queryFn: async () => {
      if (!userProfile?.points && userProfile?.points !== 0) return null;
      
      const { data, error } = await supabase
        .from('member_levels')
        .select('*')
        .lte('min_points', userProfile.points)
        .gte('max_points', userProfile.points)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user level:', error);
        return null;
      }
      
      return data;
    },
    enabled: userProfile !== undefined
  });

  // Helper function to safely get benefits as array
  const getUserBenefits = (benefits: any): string[] => {
    if (Array.isArray(benefits)) {
      return benefits as string[];
    }
    if (typeof benefits === 'string') {
      try {
        const parsed = JSON.parse(benefits);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Helper functions to get localized content
  const getRewardName = (reward: Reward) => {
    if (language === 'en' && reward.name_en) return reward.name_en;
    return reward.name;
  };

  const getRewardDescription = (reward: Reward) => {
    if (language === 'en' && reward.description_en) return reward.description_en;
    return reward.description;
  };

  const getRuleName = (rule: PointsRule) => {
    if (language === 'en' && rule.action_name_en) return rule.action_name_en;
    return rule.action_name;
  };

  const getRuleDescription = (rule: PointsRule) => {
    if (language === 'en' && rule.description_en) return rule.description_en;
    return rule.description;
  };

  // Helper function to get localized redemption reward name
  const getRedemptionRewardName = (redemption: RedemptionRecord) => {
    if (language === 'en' && redemption.rewards?.name_en) {
      return redemption.rewards.name_en;
    }
    return redemption.rewards?.name || '';
  };

  // Helper function to get localized redemption reward description
  const getRedemptionRewardDescription = (redemption: RedemptionRecord) => {
    if (language === 'en' && redemption.rewards?.description_en) {
      return redemption.rewards.description_en;
    }
    return redemption.rewards?.description || '';
  };

  useEffect(() => {
    fetchRewards();
    fetchPointsRules();
    if (user) {
      fetchRedemptionHistory();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const fetchPointsRules = async () => {
    try {
      const { data, error } = await supabase
        .from('points_rules')
        .select('*')
        .eq('is_active', true)
        .order('points', { ascending: false });

      if (error) throw error;
      setPointsRules(data || []);
    } catch (error) {
      console.error('Error fetching points rules:', error);
    }
  };

  const fetchRewards = async () => {
    try {
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .eq('is_active', true)
        .order('points_required', { ascending: true });

      if (error) throw error;
      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
    }
  };

  const fetchRedemptionHistory = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('reward_redemptions')
        .select(`
          *,
          rewards (
            name,
            name_en,
            description,
            description_en
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRedemptionHistory(data || []);
    } catch (error) {
      console.error('Error fetching redemption history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeem = async (rewardId: string, pointsRequired: number) => {
    if (!user || !userProfile) return;

    if (userProfile.points < pointsRequired) {
      toast({
        variant: 'destructive',
        title: language === 'en' ? 'Insufficient Points' : '積分不足',
        description: language === 'en' ? 'You do not have enough points to redeem this reward.' : '您的積分不足以兌換此獎勵。',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('reward_redemptions')
        .insert({
          user_id: user.id,
          reward_id: rewardId,
          points_spent: pointsRequired,
          status: 'pending'
        });

      if (error) throw error;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ points: userProfile.points - pointsRequired })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: language === 'en' ? 'Redemption Successful' : '兌換成功',
        description: language === 'en' ? 'Your reward has been successfully redeemed!' : '您的獎勵已成功兌換！',
      });

      fetchRewards();
      fetchRedemptionHistory();
      window.location.reload();
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast({
        variant: 'destructive',
        title: language === 'en' ? 'Redemption Failed' : '兌換失敗',
        description: language === 'en' ? 'Failed to redeem reward. Please try again later.' : '兌換獎勵失敗，請稍後再試。',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return language === 'en' ? 'Completed' : '已完成';
      case 'pending':
        return language === 'en' ? 'Pending' : '處理中';
      case 'cancelled':
        return language === 'en' ? 'Cancelled' : '已取消';
      default:
        return status;
    }
  };

  const getTaskColorClass = (actionType: string) => {
    switch (actionType) {
      case 'review_submit':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'photo_upload':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'profile_complete':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'weekly_login':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'share_click':
        return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'referral_bonus':
        return 'bg-pink-50 border-pink-200 text-pink-800';
      case 'special_event':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (isLoading || levelLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-600">
              {language === 'en' ? 'Loading...' : '載入中...'}
            </span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Points Center' : '積分中心'}
            </h1>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {language === 'en' 
                ? 'Share rental experiences, help others avoid bad landlords, and earn TruStay points!'
                : '分享租屋經驗、幫助他人避開壞房東，就能賺取 TruStay 積分！'
              }
            </p>
          </div>
        </section>

        {/* Tabs Navigation */}
        <section className="px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto">
            <Tabs defaultValue="summary" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white border">
                <TabsTrigger value="summary" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  {language === 'en' ? 'Overview' : '積分總覽'}
                </TabsTrigger>
                <TabsTrigger value="tasks" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  {language === 'en' ? 'Tasks' : '任務清單'}
                </TabsTrigger>
                <TabsTrigger value="rewards" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  {language === 'en' ? 'Rewards' : '兌換獎勵'}
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" disabled={!isLoggedIn}>
                  {!isLoggedIn && <Lock className="h-4 w-4 mr-1" />}
                  {language === 'en' ? 'History' : '兌換紀錄'}
                </TabsTrigger>
              </TabsList>

              {/* Points Overview */}
              <TabsContent value="summary" className="mt-8">
                {/* Points System Introduction */}
                <div className="mb-8 p-8 bg-white rounded-xl border-l-4 border-orange-500 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {language === 'en' ? 'Points System Introduction' : '積分制度簡介'}
                  </h2>
                  <p className="text-gray-700 text-lg mb-6">
                    {language === 'en' 
                      ? 'Earn points by completing tasks and redeem them for useful rewards! New prizes are available every month.'
                      : '透過完成任務獲得積分，積分可兌換實用獎勵！每個月都有新的獎品等您來兌換。'
                    }
                  </p>
                  
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {language === 'en' ? 'Points can be used for:' : '積分可用於：'}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-700">
                          {language === 'en' ? 'Redeem useful rewards' : '兌換實用獎勵'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-700">
                          {language === 'en' ? 'Unlock member titles' : '解鎖會員頭銜'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-700">
                          {language === 'en' ? 'Join monthly lottery' : '參加每月抽獎'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-700">
                          {language === 'en' ? 'Download exclusive guides' : '下載專屬指南'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Member Status */}
                {isLoggedIn ? (
                  <div className="p-8 bg-white rounded-xl border-l-4 border-orange-500 shadow-sm">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">
                      {language === 'en' ? 'Member Status' : '會員狀態'}
                    </h2>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="bg-orange-100 rounded-xl p-6 text-center">
                        <div className="text-2xl font-bold text-orange-600 mb-2">
                          {userLevel?.name || (language === 'en' ? 'New Agent' : '新手探員')}
                        </div>
                        <div className="text-sm text-gray-600">
                          {language === 'en' ? 'My Title' : '我的頭銜'}
                        </div>
                      </div>
                      <div className="bg-orange-100 rounded-xl p-6 text-center">
                        <div className="text-3xl font-bold text-orange-600 mb-2">
                          {userProfile?.points || 0}
                        </div>
                        <div className="text-sm text-gray-600">
                          {language === 'en' ? 'My Points' : '我的積分'}
                        </div>
                      </div>
                      <div className="bg-orange-100 rounded-xl p-6 text-center">
                        <div className="text-2xl font-bold text-orange-600 mb-2">
                          {userProfile?.is_verified 
                            ? (language === 'en' ? 'Verified' : '已驗證')
                            : (language === 'en' ? 'Unverified' : '未驗證')
                          }
                        </div>
                        <div className="text-sm text-gray-600">
                          {language === 'en' ? 'Member Status' : '會員狀態'}
                        </div>
                      </div>
                    </div>

                    {userLevel && (() => {
                      const benefits = getUserBenefits(userLevel.benefits);
                      return benefits.length > 0 ? (
                        <div className="mt-6">
                          <div className="text-lg font-semibold text-gray-900 mb-3">
                            {language === 'en' ? 'Member Benefits:' : '會員權益：'}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {benefits.map((benefit, index) => (
                              <Badge key={index} variant="secondary" className="text-orange-700 bg-orange-100">
                                <Award className="h-3 w-3 mr-1" />
                                {benefit}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                ) : (
                  <div className="p-8 bg-white rounded-xl border-l-4 border-gray-300 shadow-sm text-center">
                    <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {language === 'en' ? 'Login to View Your Points Status' : '登入查看您的積分狀態'}
                    </h2>
                    <p className="text-gray-600 mb-6">
                      {language === 'en' ? 'Login to view your points, titles, and member benefits' : '登入後即可查看積分、頭銜和會員權益'}
                    </p>
                    <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                      <Link to="/login">
                        {language === 'en' ? 'Login Now' : '立即登入'}
                      </Link>
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Tasks List */}
              <TabsContent value="tasks" className="mt-8">
                {/* How to Earn Points */}
                <Card className="border-l-4 border-orange-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Medal className="h-5 w-5 text-orange-500" />
                      {language === 'en' ? 'How to Earn Points' : '如何獲得積分'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pointsRules.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pointsRules.map((rule) => (
                          <div key={rule.id} className={`p-6 rounded-lg border-2 ${getTaskColorClass(rule.action_type)}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-lg">{getRuleName(rule)}</h3>
                              <Badge variant="secondary" className="ml-2">
                                +{rule.points} {language === 'en' ? 'Points' : '積分'}
                              </Badge>
                            </div>
                            <p className="text-sm mb-3">{getRuleDescription(rule)}</p>
                            <div className="flex items-center justify-between text-xs">
                              <span className={rule.is_repeatable ? 'text-blue-600' : 'text-gray-500'}>
                                {rule.is_repeatable 
                                  ? (language === 'en' ? 'Repeatable' : '可重複獲得')
                                  : (language === 'en' ? 'One-time reward' : '一次性獎勵')
                                }
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Medal className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {language === 'en' ? 'No Points Tasks Available' : '暫無積分任務'}
                        </h3>
                        <p className="text-gray-600">
                          {language === 'en' ? 'Please check back later for new point tasks!' : '請稍後查看新的積分任務！'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Rewards */}
              <TabsContent value="rewards" className="mt-8">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {rewards.map((reward) => (
                    <Card key={reward.id} className="overflow-hidden border-l-4 border-orange-500">
                      {reward.image_url && (
                        <div className="aspect-video relative overflow-hidden">
                          <img
                            src={reward.image_url}
                            alt={getRewardName(reward)}
                            className="object-cover w-full h-full"
                          />
                        </div>
                      )}
                      <CardHeader>
                        <CardTitle className="text-lg">{getRewardName(reward)}</CardTitle>
                        {getRewardDescription(reward) && (
                          <p className="text-sm text-gray-600">{getRewardDescription(reward)}</p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500" />
                            <span className="font-semibold">{reward.points_required}</span>
                            <span className="text-sm text-gray-600">
                              {language === 'en' ? 'Points' : '積分'}
                            </span>
                          </div>
                        </div>
                        {reward.stock_quantity !== null && reward.stock_quantity <= 10 && (
                          <div className="text-sm text-orange-600 mb-2">
                            {language === 'en' 
                              ? `Only ${reward.stock_quantity} left!`
                              : `僅剩 ${reward.stock_quantity} 個！`
                            }
                          </div>
                        )}
                        {isLoggedIn ? (
                          <Button
                            onClick={() => handleRedeem(reward.id, reward.points_required)}
                            disabled={!userProfile || userProfile.points < reward.points_required || reward.stock_quantity === 0}
                            className="w-full bg-orange-500 hover:bg-orange-600"
                          >
                            <Gift className="h-4 w-4 mr-2" />
                            {userProfile && userProfile.points >= reward.points_required 
                              ? (language === 'en' ? 'Redeem' : '兌換')
                              : (language === 'en' ? 'Insufficient Points' : '積分不足')
                            }
                          </Button>
                        ) : (
                          <Button asChild className="w-full bg-gray-500 hover:bg-gray-600">
                            <Link to="/login">
                              <Lock className="h-4 w-4 mr-2" />
                              {language === 'en' ? 'Login to Redeem' : '登入後兌換'}
                            </Link>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {rewards.length === 0 && (
                  <Card className="border-l-4 border-gray-300">
                    <CardContent className="p-12">
                      <div className="text-center">
                        <Gift className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {language === 'en' ? 'No Rewards Available' : '暫無可用獎勵'}
                        </h3>
                        <p className="text-gray-600">
                          {language === 'en' ? 'Please check back later for new rewards!' : '請稍後查看新的獎勵！'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Redemption History */}
              <TabsContent value="history" className="mt-8">
                {isLoggedIn ? (
                  <Card className="border-l-4 border-orange-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <History className="h-5 w-5" />
                        {language === 'en' ? 'Redemption History' : '兌換紀錄'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {redemptionHistory.length === 0 ? (
                        <div className="text-center py-12">
                          <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {language === 'en' ? 'No Redemption History' : '暫無兌換紀錄'}
                          </h3>
                          <p className="text-gray-600">
                            {language === 'en' ? 'You haven\'t made any redemptions yet' : '您還沒有進行任何兌換'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {redemptionHistory.map((record) => (
                            <div key={record.id} className="border rounded-lg p-4">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="font-semibold">
                                    {getRedemptionRewardName(record)}
                                  </h4>
                                  {getRedemptionRewardDescription(record) && (
                                    <p className="text-sm text-gray-600">
                                      {getRedemptionRewardDescription(record)}
                                    </p>
                                  )}
                                </div>
                                <Badge className={getStatusColor(record.status)}>
                                  {getStatusText(record.status)}
                                </Badge>
                              </div>
                              <div className="flex justify-between items-center text-sm text-gray-600">
                                <span>
                                  {language === 'en' ? 'Points Spent:' : '消費積分:'} {record.points_spent}
                                </span>
                                <span>{new Date(record.created_at).toLocaleDateString(language === 'en' ? 'en-CA' : 'zh-TW')}</span>
                              </div>
                              {record.notes && (
                                <div className="mt-2 text-sm text-gray-600">
                                  {language === 'en' ? 'Notes:' : '備註:'} {record.notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-l-4 border-gray-300">
                    <CardContent className="p-12">
                      <div className="text-center">
                        <Lock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {language === 'en' ? 'Please Login First' : '請先登入'}
                        </h3>
                        <p className="text-gray-600 mb-6">
                          {language === 'en' ? 'Login to view your redemption history' : '登入後即可查看您的兌換紀錄'}
                        </p>
                        <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                          <Link to="/login">
                            {language === 'en' ? 'Login Now' : '立即登入'}
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default Points;
